import { TooltipDirective } from '../../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../../componentes/botones';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { EstadoEmergenciaMedica, IEmergencia } from 'modelos/src';
import {
  ESTADOS_EMERGENCIA,
  ESTADOS_FINALES,
  PRIORIDADES,
  estadoDe,
  presentacionEstado,
  presentacionPrioridad,
} from '../../../estado-emergencia';
import { SeccionesPlegablesDirective } from '../../../../../../componentes/mapa-panel/secciones-plegables.directive';

export type CapaEmergencias =
  | 'emergencias'
  | 'ambulancias'
  | 'hospitales'
  | 'bases';

export type CapasEmergencias = Record<CapaEmergencias, boolean>;

export type ModoIconoEmergencia = 'prioridad' | 'estado';

interface IChipFiltro {
  clave: string;
  label: string;
  color: string;
  icono: string;
  cant: number;
}

const STORAGE_CAPAS = 'mapaEmergenciasMedicas.capas';
const STORAGE_MODO = 'mapaEmergenciasMedicas.modo';

/** Prioridad con la que se dibuja el pin (la de cierre manda si existe). */
export function prioridadMapa(e: IEmergencia): string {
  return e.prioridadCierre || e.prioridadApertura || '';
}

@Component({
  selector: 'app-flotante-emergencias-medicas',
  templateUrl: './flotante-emergencias-medicas.component.html',
  styleUrl: './flotante-emergencias-medicas.component.scss',
  imports: [
    TooltipDirective,
    FormsModule,
    MatExpansionModule,
    MatIconModule,
    BOTONES,
    SeccionesPlegablesDirective,
    NgTemplateOutlet,
  ],
})
export class FlotanteEmergenciasMedicasComponent implements OnInit, OnChanges {
  @Input() emergencias: IEmergencia[] = [];
  @Input() cantAmbulancias = 0;
  @Input() cantHospitales = 0;
  @Input() cantBases = 0;
  /** Dona del resumen; va junto a los chips de Código o de Estados. */
  @Input() dona: TemplateRef<unknown> | null = null;

  @Output() emergenciasChange = new EventEmitter<IEmergencia[]>();
  @Output() capasChange = new EventEmitter<CapasEmergencias>();
  @Output() modoChange = new EventEmitter<ModoIconoEmergencia>();

  public expansionPanelExpanded = true;
  public textoBusqueda = '';
  public capas: CapasEmergencias = {
    emergencias: true,
    ambulancias: true,
    hospitales: true,
    bases: true,
  };
  public modo: ModoIconoEmergencia = 'prioridad';

  // Claves activas de cada filtro (todas = sin filtrar)
  private readonly todasPrioridades: string[] = [...PRIORIDADES, ''];
  private readonly todosEstados: string[] = ESTADOS_EMERGENCIA.filter(
    (e) => !ESTADOS_FINALES.includes(e),
  );
  public filtrosPrioridad: string[] = [...this.todasPrioridades];
  public filtrosEstado: string[] = [...this.todosEstados];

  public chipsPrioridad: IChipFiltro[] = [];
  public chipsEstado: IChipFiltro[] = [];
  public filtradas: IEmergencia[] = [];

  public readonly capasConfig: {
    clave: CapaEmergencias;
    label: string;
    icono: string;
  }[] = [
    {
      clave: 'emergencias',
      label: 'Emergencias',
      icono: 'emergency',
    },
    {
      clave: 'ambulancias',
      label: 'Ambulancias',
      icono: 'airport_shuttle',
    },
    {
      clave: 'hospitales',
      label: 'Hospitales',
      icono: 'local_hospital',
    },
    {
      clave: 'bases',
      label: 'Bases',
      icono: 'apartment',
    },
  ];

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent) {
    const esCelular = (event.target as Window).innerWidth <= 768;
    if (esCelular === this.expansionPanelExpanded) {
      this.expansionPanelExpanded = !esCelular;
    }
  }

  public cantidadCapa(capa: CapaEmergencias): number {
    switch (capa) {
      case 'emergencias':
        return this.emergencias?.length ?? 0;
      case 'ambulancias':
        return this.cantAmbulancias;
      case 'hospitales':
        return this.cantHospitales;
      case 'bases':
        return this.cantBases;
    }
  }

  public get hayFiltrosActivos(): boolean {
    return (
      this.textoBusqueda.trim().length > 0 ||
      this.hayPrioridadesFiltradas ||
      this.hayEstadosFiltrados
    );
  }

  public get hayPrioridadesFiltradas(): boolean {
    return this.filtrosPrioridad.length !== this.todasPrioridades.length;
  }

  public get hayEstadosFiltrados(): boolean {
    return this.filtrosEstado.length !== this.todosEstados.length;
  }

  // ---------------------------------------------------------------------------
  // Filtrado

  private normalizar(texto: string): string {
    return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  }

  private coincideBusqueda(e: IEmergencia, termino: string): boolean {
    if (!termino) return true;
    const t = this.normalizar(termino);
    const p = e.destinatarioAsistencia;
    const campos = [
      e.codigo !== undefined ? String(e.codigo) : '',
      e.codigo !== undefined ? `#${e.codigo}` : '',
      e.direccion ?? '',
      e.solicitante ?? '',
      [p?.nombre, p?.apellido].filter(Boolean).join(' '),
      p?.dni !== undefined ? String(p.dni) : '',
    ];
    return campos.some((c) => this.normalizar(c).includes(t));
  }

  private estadoClave(e: IEmergencia): string {
    return estadoDe(e) || 'Pendiente';
  }

  private prioridadClave(e: IEmergencia): string {
    const p = prioridadMapa(e);
    return this.todasPrioridades.includes(p) ? p : '';
  }

  public filtrar(): void {
    const termino = this.textoBusqueda.trim();
    const base = (this.emergencias ?? []).filter((e) =>
      this.coincideBusqueda(e, termino),
    );

    // Cada grupo de chips cuenta con el otro filtro aplicado
    const porEstado = base.filter((e) =>
      this.filtrosEstado.includes(this.estadoClave(e)),
    );
    const porPrioridad = base.filter((e) =>
      this.filtrosPrioridad.includes(this.prioridadClave(e)),
    );

    this.chipsPrioridad = this.todasPrioridades
      .map((clave) => {
        const p = presentacionPrioridad(clave);
        return {
          clave,
          label: p.label,
          color: p.color,
          icono: p.icono,
          cant: porEstado.filter((e) => this.prioridadClave(e) === clave)
            .length,
        };
      })
      // "Sin código" sólo si hay alguna
      .filter((c) => c.clave !== '' || c.cant > 0);

    this.chipsEstado = this.todosEstados.map((clave) => {
      const p = presentacionEstado(clave);
      return {
        clave,
        label: p.label,
        color: p.color,
        icono: p.icono,
        cant: porPrioridad.filter((e) => this.estadoClave(e) === clave).length,
      };
    });

    this.filtradas = porEstado.filter((e) =>
      this.filtrosPrioridad.includes(this.prioridadClave(e)),
    );
    this.emergenciasChange.emit(this.filtradas);
  }

  public onBusquedaChange(): void {
    this.filtrar();
  }

  public limpiarBusqueda(): void {
    this.textoBusqueda = '';
    this.filtrar();
  }

  private toggle(lista: string[], clave: string): void {
    const i = lista.indexOf(clave);
    if (i > -1) lista.splice(i, 1);
    else lista.push(clave);
  }

  public togglePrioridad(clave: string): void {
    this.toggle(this.filtrosPrioridad, clave);
    this.filtrar();
  }

  public toggleEstado(clave: string): void {
    this.toggle(this.filtrosEstado, clave);
    this.filtrar();
  }

  public limpiarPrioridades(): void {
    this.filtrosPrioridad = [...this.todasPrioridades];
    this.filtrar();
  }

  public limpiarEstados(): void {
    this.filtrosEstado = [...this.todosEstados];
    this.filtrar();
  }

  public limpiarFiltros(): void {
    this.textoBusqueda = '';
    this.filtrosPrioridad = [...this.todasPrioridades];
    this.filtrosEstado = [...this.todosEstados];
    this.filtrar();
  }

  public estadoActivo(clave: string): boolean {
    return this.filtrosEstado.includes(clave as EstadoEmergenciaMedica);
  }

  // ---------------------------------------------------------------------------
  // Capas y modo

  public toggleCapa(capa: CapaEmergencias): void {
    this.capas = { ...this.capas, [capa]: !this.capas[capa] };
    this.guardar(STORAGE_CAPAS, JSON.stringify(this.capas));
    this.capasChange.emit(this.capas);
  }

  public setModo(modo: ModoIconoEmergencia): void {
    if (this.modo === modo) return;
    this.modo = modo;
    this.guardar(STORAGE_MODO, modo);
    this.modoChange.emit(modo);
  }

  private guardar(clave: string, valor: string): void {
    try {
      localStorage.setItem(clave, valor);
    } catch {
      // Sin storage: vale sólo para esta visita
    }
  }

  private leer(clave: string): string | null {
    try {
      return localStorage.getItem(clave);
    } catch {
      return null;
    }
  }

  ngOnInit(): void {
    this.expansionPanelExpanded = window.innerWidth > 768;

    try {
      const capas = JSON.parse(this.leer(STORAGE_CAPAS) || 'null');
      if (capas && typeof capas === 'object') {
        this.capas = { ...this.capas, ...capas };
      }
    } catch {
      // valor corrupto: quedan los defaults
    }
    const modo = this.leer(STORAGE_MODO);
    if (modo === 'estado' || modo === 'prioridad') this.modo = modo;

    this.capasChange.emit(this.capas);
    this.modoChange.emit(this.modo);
    this.filtrar();
  }

  ngOnChanges(): void {
    this.filtrar();
  }
}
